import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { Month, getMonthsForFiscalYear, todayYMD } from '@utils/date-utils'

import { ACTUALS } from '@components/financial/custom-kpis/const'
import { IModelSelection, IModelSelectionValue } from '@components/financial/custom-kpis/types'

import { rehydrateStore } from '@store/actions/rehydrate-store'

import { YearInfo } from 'types/filter'

import { RootState } from '..'

interface CustomKpisView {
  yearModelSelections: Record<number, IModelSelection>
  yearInfo: YearInfo
  latestMonthSelection: string[]
}

const defaultModelSelections = _.zipObject(
  getMonthsForFiscalYear(),
  new Array(12).fill(ACTUALS)
) as IModelSelection

const initialState: CustomKpisView = {
  yearModelSelections: {},
  yearInfo: todayYMD(undefined),
  latestMonthSelection: []
}

const customKpisSlice = createSlice({
  name: '@CUSTOM_KPIS',
  initialState,
  reducers: {
    changeModelSelections: (
      state,
      action: PayloadAction<{
        month: Month | Month[]
        modelId: IModelSelectionValue
      }>
    ) => {
      const { month, modelId } = action.payload
      const year = state.yearInfo.year
      const modelSelections = _.get(
        state.yearModelSelections,
        year,
        _.clone(defaultModelSelections)
      ) as IModelSelection

      const months = Array.isArray(month) ? month : [month]
      months.forEach((m) => {
        modelSelections[m] = modelId
        state.latestMonthSelection.push(m)
      })

      state.yearModelSelections[year] = modelSelections
    },
    setYear: (state, action: PayloadAction<YearInfo>) => {
      state.yearInfo = action.payload
    },
    resetLatestMonthSelection: (state) => {
      state.latestMonthSelection = []
    }
  },
  extraReducers: (builder) => {
    builder.addCase(rehydrateStore, (_state, action) => {
      return { ...initialState, ...action.payload.customKpis }
    })
  }
})

export const { changeModelSelections, setYear, resetLatestMonthSelection } = customKpisSlice.actions

export const customKpisReducer = customKpisSlice.reducer

export const selectModelSelections = (state: RootState) =>
  _.get(
    state.customKpis.yearModelSelections,
    state.customKpis.yearInfo.year,
    defaultModelSelections
  )

export const selectYear = (state: RootState) =>
  _.get(state, ['customKpis', 'yearInfo'], initialState.yearInfo)

export const selectLatestMonthSelection = (state: RootState) =>
  state.customKpis.latestMonthSelection
